import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LifeBuoy, Mail, Phone, Copy, CheckCircle2, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Small "Help & Support" dialog surfaced from the settings menu.
// Answers Intuit's Error-Handling Q4: "Do you provide a way for customers to
// contact you for support from within your app?"  Also shows the latest
// intuit_tid so the user can quote it in a support ticket.
const HelpDialog = ({ open, onOpenChange }) => {
  const [tid, setTid] = useState(null);
  const [lastError, setLastError] = useState(null);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/qbo/status")
      .then((r) => {
        setTid(r.data?.last_refresh_tid || null);
        setLastError(r.data?.last_error || null);
      })
      .catch(() => { setTid(null); setLastError(null); });
  }, [open]);

  const copy = (label, val) => {
    if (!val) return;
    navigator.clipboard?.writeText(val).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(""), 1500);
    });
  };

  const mailto = `mailto:support@elitemechanical.solutions?subject=Elite%20Portal%20support%20request${tid ? `&body=Reference%20intuit_tid%3A%20${encodeURIComponent(tid)}` : ""}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-[calc(100vw-1.5rem)] overflow-y-auto bg-[#0d0d0d] text-white sm:max-w-md sm:w-full"
        data-testid="help-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-emerald-400" /> Help &amp; Support
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Reach the Elite Mechanical team directly.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          <a
            href={mailto}
            data-testid="support-email-link"
            className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 hover:border-emerald-400/40 hover:bg-emerald-500/[0.06]"
          >
            <Mail className="h-4 w-4 text-emerald-300" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white">Email support</p>
              <p className="truncate text-xs text-white/50">support@elitemechanical.solutions</p>
            </div>
            <ExternalLink className="h-3.5 w-3.5 text-white/40" />
          </a>

          <a
            href="tel:7055626494"
            data-testid="support-phone-link"
            className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 hover:border-emerald-400/40 hover:bg-emerald-500/[0.06]"
          >
            <Phone className="h-4 w-4 text-emerald-300" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white">Call the office</p>
              <p className="text-xs text-white/50">705-562-6494 · Mon–Fri, 8 AM – 5 PM ET</p>
            </div>
          </a>

          {(tid || lastError) && (
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
              <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
                QuickBooks diagnostics
              </p>
              {tid && (
                <div className="flex items-center gap-2">
                  <p className="min-w-0 flex-1 truncate font-mono text-[11px] text-white/70" data-testid="help-last-tid">
                    <span className="text-emerald-300">intuit_tid</span>: {tid}
                  </p>
                  <button
                    onClick={() => copy("tid", tid)}
                    data-testid="help-copy-tid-btn"
                    className="rounded border border-white/15 bg-white/[0.03] p-1 text-white/60 hover:border-emerald-400/40 hover:text-white"
                  >
                    {copied === "tid" ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </div>
              )}
              {lastError && (
                <p className="mt-1.5 font-mono text-[10px] text-red-300">
                  Last error {lastError.status} at {lastError.at?.slice(0, 19)} · quote tid {lastError.intuit_tid || "n/a"}
                </p>
              )}
              <p className="mt-2 text-[11px] text-white/40">
                Include the tid above when contacting support — it lets Intuit / us trace the exact request in seconds.
              </p>
            </div>
          )}

          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-[11px] text-white/40">
            Legal:{" "}
            <a href="/legal/privacy" className="text-emerald-300 underline hover:text-emerald-200">Privacy policy</a>
            {" · "}
            <a href="/legal/eula" className="text-emerald-300 underline hover:text-emerald-200">Terms / EULA</a>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => onOpenChange?.(false)}
              data-testid="help-close-btn"
              variant="outline"
              className="border-white/20 bg-transparent text-white hover:bg-white/10"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HelpDialog;
